import React, { useEffect, useRef, useState } from 'react';

/**
 * HeroRotator — left-aligned hero over a crossfading video background.
 *
 * How it works:
 *   - A single timer drives rotation. Every SEGMENT_DURATION_MS the component
 *     advances to the next profession. The headline profession word and the
 *     active video layer swap at the same instant, so they're always in sync.
 *   - Two <video> layers stacked via position: absolute. While one layer is
 *     visible, the other preloads the next clip. On swap, CSS opacity
 *     transition crossfades over CROSSFADE_MS.
 *   - Content is left-justified inside a narrow column so the right side of
 *     the video stays visible (not covered by copy). A left-to-right gradient
 *     darkens only the left portion for text legibility.
 *
 * Edit the PROFESSIONS array to change clips / labels. Keep each clip close
 * to SEGMENT_DURATION_MS in length, muted, and similarly color-graded so
 * crossfades feel like chapters of one story.
 */

// Hero videos are served from CloudFront (distribution E1RL32NFXJGQOQ,
// behavior /videos/* → S3 bucket ehandshake-public-assets/videos/hero/).
// Override via REACT_APP_HERO_VIDEO_BASE_URL if the CDN domain ever moves
// (e.g., when the app rebrands to a new domain).
const VIDEO_BASE_URL =
  process.env.REACT_APP_HERO_VIDEO_BASE_URL ||
  'https://ehandshake.com/videos/hero';

// Top 6 professions by BNI chapter frequency plus one blue-collar trade —
// the core referral network that benefits most from team amplification.
// Each entry carries its own article ("a" vs "an") so grammar stays
// correct even when new professions are added.
// Optional per-profession framing:
//   objectPosition — pans the visible crop when the hero aspect is narrower
//     than the video. No-op on ultra-wide hero aspects where no horizontal
//     crop is happening.
//   transform / transformOrigin — zooms the clip so overflow is clipped by
//     the section's overflow-hidden, effectively panning the subject. This
//     works at ANY aspect ratio because the scale itself creates the overflow.
//     `scale(1.2)` + `left center` origin pushes subject ~10% rightward and
//     crops the right 20% of the scaled frame.
const PROFESSIONS = [
  { key: 'realtor',    article: 'a',  label: 'real estate agent', file: 'realtor.mp4',   transform: 'scale(1.2)', transformOrigin: 'left center' },
  { key: 'advisor',    article: 'a',  label: 'financial advisor', file: 'advisor.mp4'   },
  { key: 'insurance',  article: 'an', label: 'insurance agent',   file: 'insurance.mp4' },
  { key: 'lawyer',     article: 'a',  label: 'lawyer',            file: 'lawyer.mp4'    },
  { key: 'cpa',        article: 'a',  label: 'CPA',               file: 'cpa.mp4'       },
  { key: 'mortgage',   article: 'a',  label: 'mortgage broker',   file: 'mortgage.mp4'  },
  { key: 'landscaper', article: 'a',  label: 'landscaper',        file: 'landscape.mp4' },
];

const videoUrlFor = (profession) => `${VIDEO_BASE_URL}/${profession.file}`;

// "Not a ______" line also rotates in parallel with the profession. Any
// struggle can pair with any profession — order here is independent of
// PROFESSIONS. Keep the arrays the same length so every slot has a partner.
const STRUGGLES = [
  { key: 'dept',    article: 'a',  label: 'marketing guru' },
  { key: 'seo',     article: 'an', label: 'SEO expert'           },
  { key: 'graphic', article: 'a',  label: 'graphic designer'     },
  { key: 'smm',     article: 'a',  label: 'social media manager' },
  { key: 'brand',   article: 'a',  label: 'brand strategist'     },
  { key: 'video',   article: 'a',  label: 'video editor'         },
  { key: 'digital', article: 'a',  label: 'digital creator'     },
];

const SEGMENT_DURATION_MS = 5000;  // Time each profession stays on screen
const CROSSFADE_MS        = 800;   // Video + word fade duration

export default function HeroRotator() {
  // tickCount is a never-modulo'd rotation counter. activeIndex and
  // activeLayer are both derived from it — this way the layer alternation
  // stays correct regardless of whether PROFESSIONS.length is even or odd
  // (with an odd-length array, plain `activeIndex % 2` would collide on
  // wrap-around and skip the crossfade once per full cycle).
  const [tickCount, setTickCount] = useState(0);
  const videoARef = useRef(null);
  const videoBRef = useRef(null);

  const activeIndex = tickCount % PROFESSIONS.length;
  const activeLayer = tickCount % 2 === 0 ? 'A' : 'B';

  // Timer advances the tick only. All DOM side effects (play / preload)
  // live in the effect below, so StrictMode's double-invoked updaters
  // can't duplicate them and throw the rotation off by one.
  useEffect(() => {
    const id = setInterval(() => {
      setTickCount((prev) => prev + 1);
    }, SEGMENT_DURATION_MS);
    return () => clearInterval(id);
  }, []);

  // React to index changes:
  //   - Set the active layer's src to the current profession and play.
  //   - Wait for the crossfade to complete, THEN preload the next profession
  //     into the idle layer. Delaying avoids a brief flash of the next-next
  //     clip's first frame on the fading-out layer.
  useEffect(() => {
    const activeEl = activeLayer === 'A' ? videoARef.current : videoBRef.current;
    const idleEl   = activeLayer === 'A' ? videoBRef.current : videoARef.current;

    const nextIndex = (activeIndex + 1) % PROFESSIONS.length;

    const applyFraming = (el, prof) => {
      el.style.objectPosition   = prof.objectPosition   || 'center';
      el.style.transform        = prof.transform        || 'none';
      el.style.transformOrigin  = prof.transformOrigin  || 'center center';
    };

    // Safari (esp. in Low Power / energy-saver mode) will sometimes defer
    // autoplay when play() is called before the clip has enough buffered
    // data. When that happens, the browser surfaces its native "tap to
    // start" overlay (a yellow play triangle in Safari's current theme).
    // We try play() immediately AND re-try on canplay so that the moment
    // the browser signals readiness we pick it up without any overlay
    // ever surfacing. CSS also hides the native fallback button as a
    // second line of defense.
    let canplayRetry;
    if (activeEl) {
      const desiredSrc = videoUrlFor(PROFESSIONS[activeIndex]);
      if (activeEl.src !== desiredSrc) {
        activeEl.src = desiredSrc;
        activeEl.load();
      }
      applyFraming(activeEl, PROFESSIONS[activeIndex]);
      activeEl.currentTime = 0;
      activeEl.play().catch(() => {});

      canplayRetry = () => {
        if (activeEl.paused) activeEl.play().catch(() => {});
      };
      activeEl.addEventListener('canplay', canplayRetry);
    }

    const preloadTimer = setTimeout(() => {
      if (idleEl) {
        const desiredNextSrc = videoUrlFor(PROFESSIONS[nextIndex]);
        if (idleEl.src !== desiredNextSrc) {
          idleEl.pause();
          idleEl.src = desiredNextSrc;
          idleEl.load();
        }
        applyFraming(idleEl, PROFESSIONS[nextIndex]);
      }
    }, CROSSFADE_MS);

    return () => {
      clearTimeout(preloadTimer);
      if (activeEl && canplayRetry) {
        activeEl.removeEventListener('canplay', canplayRetry);
      }
    };
  }, [activeIndex, activeLayer]);

  const active   = PROFESSIONS[activeIndex];
  const struggle = STRUGGLES[activeIndex % STRUGGLES.length];

  return (
    <section className="relative overflow-hidden bg-black" style={{ minHeight: '560px' }}>
      {/* Video layer A — first clip baked into the JSX src so the browser
          begins fetching during HTML parse rather than after React's effects.
          Combined with autoPlay + preload="auto", frames land in time to paint
          alongside the copy instead of flashing black for ~200ms. */}
      <video
        ref={videoARef}
        muted
        playsInline
        autoPlay
        preload="auto"
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        disablePictureInPicture
        disableRemotePlayback
        onContextMenu={(e) => e.preventDefault()}
        src={videoUrlFor(PROFESSIONS[0])}
        className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
        style={{
          opacity: activeLayer === 'A' ? 1 : 0,
          transitionDuration: `${CROSSFADE_MS}ms`,
          objectPosition:  PROFESSIONS[0].objectPosition  || 'center',
          transform:       PROFESSIONS[0].transform       || 'none',
          transformOrigin: PROFESSIONS[0].transformOrigin || 'center center',
        }}
      />
      {/* Video layer B — idle on first paint; effect assigns its src during
          the crossfade preload window. */}
      <video
        ref={videoBRef}
        muted
        playsInline
        preload="auto"
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        disablePictureInPicture
        disableRemotePlayback
        onContextMenu={(e) => e.preventDefault()}
        className="absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out"
        style={{
          opacity: activeLayer === 'B' ? 1 : 0,
          transitionDuration: `${CROSSFADE_MS}ms`,
        }}
      />

      {/* Flat 50% dim across the whole video — softens overall brightness */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'rgba(0,0,0,0)' }}
      />

      {/* Left-to-right gradient: dark on left for legibility, clear on right so the video stays visible */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.15) 65%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/* Copy — left-justified in a narrow column */}
      <div className="relative z-10 mx-auto w-[90%] max-w-6xl py-16 md:py-4">
        <div className="max-w-xl text-left text-white">
          <span className="inline-block text-[10px] md:text-[11px] font-bold uppercase tracking-[0.25em] text-white/75 mb-4">
            Built for local, small businesses
          </span>

          {/* Headline. Reserve min-height so longer labels (e.g. "real estate agent")
              that wrap to two lines don't push the copy below downward. */}
          <h1 className="font-extrabold leading-[1.1] tracking-tight text-2xl md:text-4xl">
            <span
              className="block"
              style={{ minHeight: '2.4em' /* room for 2 lines if label wraps */ }}
            >
              You&rsquo;re{' '}
              <span
                key={active.key}
                className="inline text-[#00aef7]"
                style={{
                  animation: `heroWordFade ${CROSSFADE_MS}ms ease-out`,
                }}
              >
                {active.article} {active.label}
              </span>
              .
            </span>
            <span
              className="block"
              style={{ minHeight: '1.2em' /* reserve height so line never collapses */ }}
            >
              Not{' '}
              <span
                key={`struggle-${struggle.key}`}
                className="inline text-[#00aef7]"
                style={{
                  animation: `heroWordFade ${CROSSFADE_MS}ms ease-out`,
                }}
              >
                {struggle.article} {struggle.label}
              </span>
              .
            </span>
          </h1>

          <p
            className="mt-5 text-white/90 text-sm md:text-base leading-relaxed max-w-lg rounded-xl px-4 py-3 md:px-5 md:py-4"
            style={{
              background: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
            }}
          >
            Where is your next, new customer coming from? eHandshake is built for local service businesses like yours. We use AI to understand your business, give you ideas, design your social comms, and publish custom, specific content to every social platform you choose. We even use AI to design a complete landing page for your post; so showing up online stops being one more thing on
            your "to do" list. Then we can talk about how to use eHandshake to amplify your Social presence by using teamwork.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="/?modal=signup"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#00aef7] text-white text-sm font-semibold hover:bg-[#0098d8] transition-colors shadow-lg shadow-[#00aef7]/25"
            >
              Sign up for free
            </a>
            <a
              href="#section-1"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('section-1')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                window.history.pushState(null, '', '#section-1');
              }}
              className="inline-flex items-center px-5 py-2.5 rounded-lg border border-white/40 text-white text-sm font-semibold hover:bg-white/10 transition-colors backdrop-blur-sm"
            >
              Learn More
            </a>
          </div>

          {/* Platform strip — subtle, one line */}
          <div className="mt-8 text-white/60 text-[11px] md:text-xs">
            <span className="uppercase tracking-widest text-white/40 mr-2">
              Publishes to
            </span>
            Facebook &middot; Instagram &middot; TikTok &middot; YouTube &middot; X
            &middot; LinkedIn 
          </div>
        </div>
      </div>

      <style>{`
        @keyframes heroWordFade {
          0%   { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* Hide Safari's "autoplay was deferred" start-playback overlay and
           any WebKit/Chromium media control chrome that could flash over
           the hero on slow networks. We drive playback entirely from JS. */
        section video::-webkit-media-controls-start-playback-button,
        section video::-webkit-media-controls-overlay-play-button,
        section video::-webkit-media-controls { display: none !important; -webkit-appearance: none; }
      `}</style>
    </section>
  );
}
